import React, { useEffect, useState } from "react";
import { debug } from "../../../../utils/debugLogger";

/**
 * The steps an agent takes in one turn, shown the way Claude shows its work:
 * one quiet row per tool call, a spinner while it runs, a tick and the time it
 * took when it finishes. Rows stay in the thread after the answer arrives, so
 * the owner can see what the agent actually did.
 *
 * First used by onboarding; built here so agent chat can adopt it (SGL-322).
 */

export type AgentStepStatus = "running" | "done" | "error";

export interface AgentStep {
  /** Stable id within a turn; one row per tool call. */
  id: string;
  tool: string;
  status: AgentStepStatus;
  startedAt: number;
  endedAt?: number;
  /** Own wording for steps that are not tool calls (e.g. onboarding's training). */
  labels?: { running: string; done: string; failed?: string };
}

/** Plain-English labels, one table for every agent. Never show a raw tool
 *  name to an owner. `done` defaults to the running label when not given. */
const STEP_LABELS: Record<string, { running: string; done?: string; failed?: string }> = {
  // Onboarding
  fetch_website_profile: { running: "Reading your website", done: "Read your website", failed: "Couldn't read your website" },
  output_business_profile: { running: "Building your business profile", done: "Built your business profile" },
  crawl_website: { running: "Reading your website", done: "Read your website" },
  send_support_ticket: { running: "Sending your message to our team", done: "Sent your message to our team" },
  // Research
  perplexity_search: { running: "Searching the web", done: "Searched the web" },
  web_search: { running: "Searching the web", done: "Searched the web" },
  deep_research: { running: "Researching", done: "Researched" },
  // Content
  generate_image: { running: "Generating an image", done: "Generated an image" },
  generate_image_batch: { running: "Generating your image set", done: "Generated your image set" },
  output_images: { running: "Preparing images", done: "Prepared images" },
  output_social_posts: { running: "Writing your posts", done: "Wrote your posts" },
  output_blog: { running: "Writing your blog post", done: "Wrote your blog post" },
  output_script: { running: "Writing the script", done: "Wrote the script" },
  schedule_post: { running: "Scheduling your post", done: "Scheduled your post" },
  get_posts_with_analytics: { running: "Fetching your analytics", done: "Fetched your analytics" },
  // Receptionist
  ghl_get_availability: { running: "Checking availability", done: "Checked availability" },
  ghl_book_appointment: { running: "Booking the appointment", done: "Booked the appointment" },
  list_calls: { running: "Looking up your calls", done: "Looked up your calls" },
  output_calls: { running: "Preparing your calls list", done: "Prepared your calls list" },
  scan_business_cards: { running: "Reading your cards", done: "Read your cards" },
  draft_card_intros: { running: "Writing the intro emails", done: "Wrote the intro emails" },
  output_card_contacts: { running: "Preparing your contacts", done: "Prepared your contacts" },
  ghl_search_contact: { running: "Searching contacts", done: "Searched contacts" },
  ghl_get_messages: { running: "Loading messages", done: "Loaded messages" },
  ghl_upsert_contact: { running: "Saving the contact", done: "Saved the contact" },
  ghl_tag_contact: { running: "Updating tags", done: "Updated tags" },
  ghl_send_message: { running: "Sending the message", done: "Sent the message" },
  ghl_list_conversations: { running: "Loading conversations", done: "Loaded conversations" },
  list_outreach_recipients: { running: "Finding recipients", done: "Found recipients" },
  send_outreach_campaign: { running: "Starting the outreach campaign", done: "Started the outreach campaign" },
  check_campaign_status: { running: "Checking the campaign", done: "Checked the campaign" },
  cancel_campaign: { running: "Cancelling the campaign", done: "Cancelled the campaign" },
  check_campaign_replies: { running: "Checking replies", done: "Checked replies" },
  // Visibility
  review_google_business_profile: { running: "Preparing your Google Business Profile changes", done: "Prepared your Google Business Profile changes" },
  revise_gbp_row: { running: "Updating your proposal", done: "Updated your proposal" },
  get_reddit_threads: { running: "Reading your Reddit Feed", done: "Read your Reddit Feed" },
  // SGL-417
  agent_memory: { running: "Updating memory", done: "Updated memory" },
};

const warned = new Set<string>();

export function stepLabel(tool: string, status: AgentStepStatus): string {
  const labels = STEP_LABELS[tool];
  if (!labels) {
    if (!warned.has(tool)) {
      warned.add(tool);
      debug.warn(`AgentStepTimeline: no label for tool "${tool}"`);
    }
    // Readable rather than raw ("get_reddit_threads" -> "Get reddit threads").
    const plain = tool.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
    return status === "error" ? `${plain} failed` : plain;
  }
  if (status === "error") return labels.failed ?? `${labels.running} failed`;
  return status === "running" ? labels.running : labels.done ?? labels.running;
}

/** Apply a stream event to the step list. Pure, so callers can use it in setState. */
export function applyStepEvent(
  steps: AgentStep[],
  event: { type: "start" | "end" | "error"; tool?: string },
  now: number = Date.now(),
): AgentStep[] {
  if (event.type === "start" && event.tool) {
    // tool_selected and tool_executing both announce the same call.
    if (steps.some((s) => s.tool === event.tool && s.status === "running")) return steps;
    return [...steps, { id: `${event.tool}-${steps.length}`, tool: event.tool, status: "running", startedAt: now }];
  }
  const status: AgentStepStatus = event.type === "error" ? "error" : "done";
  return steps.map((s) =>
    s.status === "running" && (!event.tool || s.tool === event.tool)
      ? { ...s, status, endedAt: now }
      : s,
  );
}

/** Mark anything still running as finished (end of turn). */
export const finishAllSteps = (steps: AgentStep[], now: number = Date.now()): AgentStep[] =>
  applyStepEvent(steps, { type: "end" }, now);

const formatDuration = (ms: number): string => {
  if (ms < 1000) return "<1s";
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

const useTicker = (active: boolean) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [active]);
};

const StepIcon: React.FC<{ status: AgentStepStatus }> = ({ status }) => {
  if (status === "running") {
    return (
      <span
        className="block h-[15px] w-[15px] animate-spin rounded-full border-[1.5px] border-theme-fg-muted/35 border-t-theme-fg-muted"
        aria-hidden
      />
    );
  }
  if (status === "error") {
    return <span className="material-icons-outlined text-[19px] leading-none text-red-500" aria-hidden>error_outline</span>;
  }
  return <span className="material-icons-outlined text-[19px] leading-none text-theme-fg-muted" aria-hidden>task_alt</span>;
};

/**
 * Claude-style: a quiet summary line ("Working…" / "Worked for 9s"), then one
 * row per step, icons joined by a thin vertical rule.
 */
export const AgentStepTimeline: React.FC<{ steps: AgentStep[]; className?: string }> = ({ steps, className = "" }) => {
  const running = steps.some((s) => s.status === "running");
  useTicker(running);
  if (steps.length === 0) return null;

  const start = Math.min(...steps.map((s) => s.startedAt));
  const end = running ? Date.now() : Math.max(...steps.map((s) => s.endedAt ?? s.startedAt));
  const failed = steps.some((s) => s.status === "error");

  return (
    <div className={className} aria-label="What the assistant did">
      <p className="mb-3 text-[14px] text-theme-fg-muted">
        {running ? "Working…" : `${failed ? "Finished" : "Worked"} for ${formatDuration(end - start)}`}
      </p>
      <ol className="flex flex-col">
        {steps.map((step, i) => {
          const elapsed = (step.endedAt ?? Date.now()) - step.startedAt;
          const last = i === steps.length - 1;
          return (
            <li
              key={step.id}
              className="flex gap-3.5"
              style={{ animation: "agentStepIn 0.25s ease both" }}
            >
              {/* icon column with the connector to the next step */}
              <div className="flex w-5 shrink-0 flex-col items-center">
                <span className="flex h-6 w-5 items-center justify-center">
                  <StepIcon status={step.status} />
                </span>
                {!last && <span className="my-1 w-px flex-1 bg-theme-border" aria-hidden />}
              </div>
              <div className={`flex min-w-0 items-baseline gap-2 ${last ? "" : "pb-4"}`}>
                <span
                  className={`text-[15px] leading-6 ${
                    step.status === "running" ? "text-theme-fg" : step.status === "error" ? "text-red-600" : "text-theme-fg/80"
                  }`}
                >
                  {step.labels
                    ? step.status === "running"
                      ? step.labels.running
                      : step.status === "error"
                        ? step.labels.failed ?? `${step.labels.running} failed`
                        : step.labels.done
                    : stepLabel(step.tool, step.status)}
                  {step.status === "running" && "…"}
                </span>
                <span className="text-[12px] tabular-nums text-theme-fg-muted/70">{formatDuration(elapsed)}</span>
              </div>
            </li>
          );
        })}
      </ol>
      <style>{`@keyframes agentStepIn { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
};

export default AgentStepTimeline;
